<template>
  <div class="expiring-plans-page">
    <HomeIcon :showArrow="true" to="adm-dashboard" />
    <div class="container">
      <h1 class="title">Planos Próximos ao Vencimento</h1>

      <!-- Loader enquanto carrega -->
      <div class="loader-container" v-if="loading">
        <div class="loader"></div>
        <p>Carregando planos...</p>
      </div>

      <!-- Lista de planos -->
      <div v-if="!loading && expiringPlans.length > 0" class="plans-grid">
        <div v-for="plan in expiringPlans" :key="plan.id" class="plan-card">
          <div class="plan-header">
            <h2 class="plan-title">{{ plan.usuario.nome }}</h2>
          </div>
          <div class="plan-details">
            <p><strong>Início:</strong> {{ formatDate(plan.dataInicio) }}</p>
            <p><strong>Término:</strong> {{ formatDate(plan.dataFim) }}</p>
            <p><strong>Faltam:</strong> {{ calculateDaysLeft(plan.dataFim) }} dias</p>
            <p><strong>Quadra:</strong> {{ plan.quadra.nome }}</p>
            <p><strong>Horários:</strong> {{ plan.horarios }}</p>
            <p><strong>Dia:</strong> {{ formatDayOfWeek(plan.indexDia) }}</p>
          </div>
          <div class="plan-actions">
            <button class="action-button notify" @click="openWhatsApp(plan.usuario.telefone, plan.usuario.nome)">
              Avisar no WhatsApp
            </button>
            <button class="action-button renew" @click="showRenewModal(plan.id)">
              Renovar
            </button>
          </div>
        </div>
      </div>

      <!-- Mensagem caso não tenha planos -->
      <div v-if="!loading && expiringPlans.length === 0" class="no-plans">
        <p>Nenhum plano expira nos próximos 5 dias.</p>
      </div>

      <!-- Modal de Confirmação -->
      <div v-if="showModal" class="modal-overlay">
        <div class="modal-container">
          <h2>Atenção!</h2>
          <p v-if="!renewing">
            Você está prestes a renovar este plano. A data de término será atualizada. Deseja continuar?
          </p>
          <!-- Exibir mensagem de erro -->
          <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
          <!-- Loader durante a renovação -->
          <div v-if="renewing" class="loader-container">
            <div class="loader"></div>
            <p>Renovando...</p>
          </div>
          <div v-else class="button-group">
            <button class="modal-button cancel" @click="closeModal()">Cancelar</button>
            <button class="modal-button confirm" @click="confirmRenew()">Confirmar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import axios from 'axios';
import dayjs from 'dayjs';
import HomeIcon from '../../components/HomeIcon.vue';

// Definir a interface dos planos
interface Plan {
  id: number;
  dataInicio: string;
  dataFim: string;
  horarios: string;
  indexDia: number;
  usuario: {
    nome: string;
    telefone: string;
  };
  quadra: {
    nome: string;
  };
}

export default defineComponent({
  name: 'AdmCheckPlans',
  components: {
    HomeIcon,
  },
  setup() {
    const loading = ref(true);
    const expiringPlans = ref<Plan[]>([]); // Array tipado com a interface Plan
    const showModal = ref(false); // Controla a exibição do modal
    const selectedPlanId = ref<number | null>(null); // Armazena o ID do plano a ser renovado
    const errorMessage = ref<string>(""); // Variável para armazenar a mensagem de erro
    const renewing = ref(false); // Variável para controlar o estado de renovação (mostrar loader)

    // Função para formatar datas
    const formatDate = (date: string) => {
      return dayjs(date).format('DD/MM/YYYY');
    };

    // Função para calcular quantos dias faltam para o plano expirar
    const calculateDaysLeft = (endDate: string) => {
      const today = dayjs().startOf('day'); // Início do dia atual (00:00)
      const expirationDate = dayjs(endDate).startOf('day'); // Início do dia de expiração (00:00)
      const diff = expirationDate.diff(today, 'day'); // Diferença em dias completos
      return diff >= 0 ? diff : 0; // Garante que o número não seja negativo
    };

    // Função para formatar o indexDia como dia da semana
    const formatDayOfWeek = (indexDia: number) => {
      const daysOfWeek = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
      return daysOfWeek[indexDia] || 'Desconhecido';
    };

    // Função para buscar os planos que estão próximos de vencer (menos de 5 dias)
    const fetchExpiringPlans = async () => {
      try {
        const response = await axios.get(`${process.env.VUE_APP_BACKEND_URL}/expiring`);
        expiringPlans.value = response.data.filter((plan: Plan) => calculateDaysLeft(plan.dataFim) <= 5);
      } catch (error) {
        console.error('Erro ao buscar os planos próximos do vencimento:', error);
      } finally {
        loading.value = false;
      }
    };

    // Função para abrir o WhatsApp com uma mensagem predefinida
    const openWhatsApp = (telefone: string, nome: string) => {
      // Remove caracteres não numéricos e formata o número com o código do Brasil (+55)
      const telefoneFormatado = telefone.replace(/[^\d]/g, ''); // Ex.: (35) 99118-4846 -> 35991184846
      const telefoneComCodigo = `+55${telefoneFormatado}`; // Adiciona o código do Brasil: +5535991184846
      const mensagem = `Olá ${nome}, seu plano no Sunset Arena está prestes a expirar. Deseja renová-lo?`;
      const url = `https://api.whatsapp.com/send?phone=${telefoneComCodigo}&text=${encodeURIComponent(mensagem)}`;
      window.open(url, '_blank');
    };

    // Função para exibir o modal de confirmação
    const showRenewModal = (planId: number) => {
      selectedPlanId.value = planId;
      showModal.value = true;
      errorMessage.value = ""; // Limpa mensagem de erro ao abrir o modal
      renewing.value = false; // Garante que o loader não esteja ativo ao abrir o modal
    };

    // Função para fechar o modal
    const closeModal = () => {
      showModal.value = false;
      selectedPlanId.value = null;
      errorMessage.value = ""; // Limpa mensagem de erro ao fechar o modal
      renewing.value = false; // Reseta o estado de renovação
    };

    // Função para confirmar a renovação
    const confirmRenew = async () => {
      if (selectedPlanId.value === null) return;

      renewing.value = true; // Ativa o loader
      errorMessage.value = ""; // Limpa mensagem de erro antes de iniciar a ação

      try {
        const response = await axios.post(`${process.env.VUE_APP_BACKEND_URL}/renovar-reserva/${selectedPlanId.value}`);
        alert(`Plano renovado com sucesso!`);
        await fetchExpiringPlans(); // Atualiza a lista após renovação
        closeModal();
      } catch (error: any) {
        console.error('Erro ao renovar plano:', error);
        // Captura a mensagem de erro detalhada do backend
        const errorDetail = error.response?.data?.message || error.response?.data?.error || error.message;
        errorMessage.value = errorDetail || "Erro ao renovar plano.";
      } finally {
        renewing.value = false; // Desativa o loader
      }
    };

    onMounted(() => {
      fetchExpiringPlans();
    });

    return {
      loading,
      expiringPlans,
      formatDate,
      calculateDaysLeft,
      formatDayOfWeek,
      openWhatsApp,
      showRenewModal,
      showModal,
      confirmRenew,
      closeModal,
      errorMessage,
      renewing, // Retorna a variável para o template
    };
  },
});
</script>

<style scoped>
/* Variáveis CSS para facilitar a manutenção */
:root {
  --primary-color: #2563eb;
  --secondary-color: #1d4ed8;
  --background-color: transparent;
  --text-color: #333;
  --light-text-color: #666;
  --button-radius: 30px;
  --card-background: #ffffff;
  --card-shadow: rgba(0, 0, 0, 0.2);
}

/* Estilo Geral */
.expiring-plans-page {
  font-family: 'Poppins', sans-serif;
  color: var(--text-color);
  background: linear-gradient(135deg, #6EE7B7 0%, #3B82F6 100%);
  min-height: 100vh;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Container Principal */
.container {
  background: var(--background-color);
  width: 100%;
  max-width: 1200px;
  padding: 40px;
  text-align: center;
}

/* Título */
.title {
  text-transform: uppercase;
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 40px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.title::after {
  content: '';
  width: 60px;
  height: 4px;
  background: #ffffff;
  display: block;
  margin: 10px auto 0;
  border-radius: 2px;
}

/* Grade de Planos */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* Exatamente 2 colunas por linha */
  gap: 20px;
  width: 100%;
}

/* Cartão de Plano */
.plan-card {
  color: white;
  background: var(--card-background);
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 6px 12px var(--card-shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 4px solid #e6e6e6;
}

.plan-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px var(--card-shadow);
  border-color: var(--primary-color);
}

/* Cabeçalho do Cartão */
.plan-header {
  margin-bottom: 15px;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 8px;
}

.plan-title {
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--primary-color);
  margin: 0;
  text-transform: uppercase;
}

/* Detalhes do Plano */
.plan-details {
  flex-grow: 1;
  font-size: 1.2rem;
}

.plan-details p {
  margin: 5px 0;
  color: var(--text-color);
}

.plan-details p strong {
  color: var(--secondary-color);
  font-weight: 600;
}

/* Ações do Plano */
.plan-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
  justify-content: center;
}

.action-button {
  padding: 8px 20px;
  border-radius: var(--button-radius);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.3s ease, transform 0.1s ease, box-shadow 0.3s ease;
}

.action-button.notify {
  background-color: #25D366; /* Cor do WhatsApp */
  color: white;
  box-shadow: 0 4px 8px rgba(37, 211, 102, 0.3);
  border: 2px solid #f3f3f3;
}

.action-button.notify:hover {
  background-color: #1EBE54;
  transform: scale(1.05);
  box-shadow: 0 6px 12px rgba(37, 211, 102, 0.4);
}

.action-button.renew {
  background-color: white;
  color: rgb(11, 79, 225);
  box-shadow: 0 4px 8px rgba(37, 99, 235, 0.3);
}

.action-button.renew:hover {
  transform: scale(1.05);
}

/* Loader */
.loader-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.loader {
  margin-top: 10px;
  margin-bottom: 5px;
  width: 50px;
  height: 50px;
  border: 8px solid #f3f3f3;
  border-radius: 50%;
  border-top: 8px solid #2563eb;
  animation: spin 1s linear infinite;
}

.loader-container p {
  font-size: 1.2rem;
  color: #ffffff;
  text-align: center;
}

/* Mensagem de "Sem Planos" */
.no-plans {
  background: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  color: var(--light-text-color);
  font-size: 1.2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

/* Modal de Confirmação (Estilo igual ao da página AdmClients) */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: #ffffff;
  width: 400px;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.modal-container h2 {
  font-size: 1.6rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #2563eb;
}

.modal-container p {
  font-size: 1rem;
  margin-bottom: 20px;
  color: #555;
}

.error-message {
  font-size: 1rem;
  color: #e74c3c !important;
  margin-bottom: 20px;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-button {
  padding: 12px 20px;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  border: none;
  transition: background 0.3s;
  width: 100%;
}

.modal-button.cancel {
  background-color: #e74c3c;
  color: #fff;
}

.modal-button.cancel:hover {
  background-color: #c0392b;
}

.modal-button.confirm {
  background-color: #2563eb;
  color: #ffffff;
}

.modal-button.confirm:hover {
  background-color: #1d4ed8;
}

/* Animações */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsividade */
@media (max-width: 768px) {
  .title {
    font-size: 2rem;
  }

  .plan-title {
    font-size: 1.2rem;
  }

  .plan-details p {
    font-size: 0.85rem;
  }

  .action-button {
    padding: 6px 15px;
    font-size: 0.8rem;
  }

  .plans-grid {
    grid-template-columns: 1fr; /* Em telas menores, empilha os cartões */
  }

  .modal-container {
    width: 90%;
    padding: 20px;
  }
}
</style>